/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @compiled/react */
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@compiled/react';
import { colors, gridSize } from '@atlaskit/theme';
import messages from './PasswordStrengthMeter.messages';
import { useIntl } from 'react-intl';
import { token } from '@atlaskit/tokens';
import { usePasswordScore } from './usePasswordScore';
import {
  getMinimumPasswordLength,
  minimumPasswordLengthMessage,
} from '../../utilities/password-policy';

const barWidths = ['0%', '20%', '40%', '60%', '80%', '100%'];

type BarProps = {
  score: number | null;
};

const Wrapper = styled.div`
  margin-bottom: ${gridSize()}px;
  padding-inline: 10px;
`;

const Bar = styled.div<BarProps>`
  background: ${colors.N40};
  height: 4px;
  margin-top: 8px;
  margin-bottom: 4px;
  position: relative;
  border-radius: 2px;

  &::before {
    background: ${props => (props.score === null ? colors.N40 : barColours[props.score])};
    bottom: 0;
    content: '';
    left: 0;
    border-radius: 2px;
    position: absolute;
    top: 0;
    transition: all 0.2s ease-out;
    width: ${props => (props.score === null ? 0 : barWidths[props.score])};
  }
`;

// BEGIN CHANGES FOR IMPROVE PASSWORD FEEDBACK EXPERIMENTS
type PasswordStrengthMeterContainerProps = BarProps & {
  strengthText: string;
  errorText?: string;
};

const barColours = [
  colors.N40, // N/A
  token('color.text.accent.red'), // Weak
  '#FFC716', // Fair
  '#94C748', // Good
  '#6A9A23', // Strong
  '#678933', // Very strong
];

const hintStyles = css({
  fontSize: '12px',
  height: '16px',
  marginTop: '4px',
  position: 'relative',
});

const errorStyle = css({ color: 'var(--Reds-R400, #DE350B)' });

type HintProps = { score: number; errorText?: string; strengthText: string };

const Hint = ({ score, errorText, strengthText }: HintProps) => {
  const styleAsError = !!errorText || score === 1;
  const textToDisplay = errorText || strengthText;
  return textToDisplay ? (
    <p css={[hintStyles, styleAsError && errorStyle]}>{textToDisplay}</p>
  ) : null;
};

const Container = (props: PasswordStrengthMeterContainerProps) => {
  return (
    <Wrapper>
      <Bar score={props.score}></Bar>
      <div role="status">
        <Hint
          score={props.score || 0}
          errorText={props.errorText}
          strengthText={props.strengthText}
        />
      </div>
    </Wrapper>
  );
};

type PasswordStrengthMeterProps = {
  i18nLevels?: string[];
  i18nNoValue?: string;
  i18nNoZxcvbn?: string;
  password: string;
  email: string;
  displayName: string;
  errorText?: string;
  passwordSelected?: boolean;
};

export const PasswordStrengthMeterExperimental = ({
  displayName,
  email,
  i18nLevels,
  i18nNoValue,
  i18nNoZxcvbn,
  password,
  errorText,
  passwordSelected,
}: PasswordStrengthMeterProps) => {
  const { formatMessage } = useIntl();
  const minimumPasswordLength = getMinimumPasswordLength();
  const strengthLevelMessages = useMemo(
    () =>
      i18nLevels ||
      [
        minimumPasswordLengthMessage,
        messages.weakPasswordAdvice,
        messages.fairPassword,
        messages.goodPassword,
        messages.strongPassword,
        messages.veryStrongPassword,
      ].map(message => formatMessage(message, { minimumPasswordLength })),
    [formatMessage, i18nLevels, minimumPasswordLength]
  );
  const noValueMessage = i18nNoValue || '';
  const noZxcvbnMessage = i18nNoZxcvbn || formatMessage(messages.passwordStrengthUnavailable);
  const { score } = usePasswordScore({ displayName, email, password });

  const getStrengthText = () => {
    if (!password && !passwordSelected) return noValueMessage;
    return typeof score === 'number' ? strengthLevelMessages[score] : noZxcvbnMessage;
  };

  return <Container errorText={errorText} strengthText={getStrengthText()} score={score} />;
};
